import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

type Step = 1 | 2 | 3 | 4;
type RegMethod = 'phone' | 'email';

interface FilePreview {
  file: File;
  preview: string;
  name: string;
}

interface WorkEntry {
  company: string;
  title: string;
  startYear: string;
  endYear: string;
  current: boolean;
  description: string;
}

const STEP_LABELS: Record<Step, string> = {
  1: '基本信息',
  2: '学历与工作',
  3: '身份认证',
  4: '生活照片',
};

export default function RegisterForm({ onSwitch }: { onSwitch: () => void }) {
  const [step, setStep] = useState<Step>(1);
  const [method, setMethod] = useState<RegMethod>('phone');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // ID docs
  const [idFront, setIdFront] = useState<FilePreview | null>(null);
  const [idBack, setIdBack] = useState<FilePreview | null>(null);
  // Education docs
  const [diplomaDoc, setDiplomaDoc] = useState<FilePreview | null>(null);
  const [transcriptDoc, setTranscriptDoc] = useState<FilePreview | null>(null);
  // Work docs
  const [workDoc, setWorkDoc] = useState<FilePreview | null>(null);
  // Life photos
  const [lifePhotos, setLifePhotos] = useState<FilePreview[]>([]);

  const idFrontRef = useRef<HTMLInputElement>(null);
  const idBackRef = useRef<HTMLInputElement>(null);
  const diplomaRef = useRef<HTMLInputElement>(null);
  const transcriptRef = useRef<HTMLInputElement>(null);
  const workDocRef = useRef<HTMLInputElement>(null);
  const lifePhotosRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    nickname: '', realName: '', gender: '', birthYear: '', phone: '',
    countryCode: '+86', email: '', password: '', smsCode: '',
    city: '', selfIntro: '',
    // Education
    eduLevel: '', eduSchool: '', eduMajor: '', eduDegree: '', eduGradYear: '',
    // Work
    occupation: '', income: '',
  });

  const [workHistory, setWorkHistory] = useState<WorkEntry[]>([
    { company: '', title: '', startYear: '', endYear: '', current: false, description: '' },
  ]);

  const sendCode = () => {
    setCodeSent(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
    }, 1000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: FilePreview | null) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setter({ file, preview: URL.createObjectURL(file), name: file.name });
  };

  const handleLifePhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const previews = files.map((f) => ({ file: f, preview: URL.createObjectURL(f), name: f.name }));
    setLifePhotos((prev) => [...prev, ...previews].slice(0, 6));
  };

  const removeLifePhoto = (idx: number) => setLifePhotos((prev) => prev.filter((_, i) => i !== idx));

  const handleChange = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));

  const updateWork = (idx: number, field: keyof WorkEntry, value: string | boolean) => {
    setWorkHistory((prev) => prev.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };

  const addWork = () => {
    if (workHistory.length < 4) {
      setWorkHistory((prev) => [...prev, { company: '', title: '', startYear: '', endYear: '', current: false, description: '' }]);
    }
  };

  const removeWork = (idx: number) => setWorkHistory((prev) => prev.filter((_, i) => i !== idx));

  const eduLabel = (v: string) => ({ phd: '博士研究生', master: '硕士研究生', bachelor: '本科', college: '大专', highschool: '高中' }[v] || v);

  const handleSubmit = async () => {
    setLoading(true);
    const body = new URLSearchParams();
    body.append('form_type', '会员注册');
    body.append('nickname', formData.nickname);
    body.append('real_name', formData.realName);
    body.append('gender', formData.gender);
    body.append('birth_year', formData.birthYear);
    body.append('city', formData.city);
    body.append('self_intro', formData.selfIntro);
    body.append('login_method', method === 'phone' ? '手机号' : '邮箱');
    if (method === 'phone') body.append('phone', `${formData.countryCode} ${formData.phone}`);
    else body.append('email', formData.email);
    body.append('education_level', eduLabel(formData.eduLevel));
    body.append('school', formData.eduSchool);
    body.append('major', formData.eduMajor);
    body.append('graduation_year', formData.eduGradYear);
    body.append('occupation', formData.occupation);
    body.append('income', formData.income);
    body.append('work_history', workHistory.map(w => `${w.company} · ${w.title} (${w.startYear}-${w.current ? '至今' : w.endYear})`).join(' | '));
    body.append('diploma_uploaded', diplomaDoc ? '已上传' : '未上传');
    body.append('transcript_uploaded', transcriptDoc ? '已上传' : '未上传');
    body.append('work_doc_uploaded', workDoc ? '已上传' : '未上传');
    body.append('id_front', idFront ? '已上传' : '未上传');
    body.append('id_back', idBack ? '已上传' : '未上传');
    body.append('life_photos_count', lifePhotos.length.toString());
    try {
      await fetch('https://readdy.ai/api/form/d7d61tfsch5bvrijfc8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-red-100 to-amber-100 rounded-full mx-auto mb-4">
          <i className="ri-shield-check-line text-red-500 text-3xl"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">注册申请已提交！</h3>
        <p className="text-gray-500 text-sm mb-2">我们将在 <strong>24-48小时内</strong> 审核您的全部证件</p>
        <p className="text-gray-500 text-sm mb-6">审核通过后，您将获得 <span className="text-red-600 font-semibold">实名认证</span>、<span className="text-red-600 font-semibold">学历认证</span> 和 <span className="text-red-600 font-semibold">视频通话权限</span></p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left mb-6">
          <p className="text-xs font-semibold text-amber-800 mb-2">审核流程：</p>
          <div className="flex flex-col gap-1.5">
            {['证件真实性核验（公安系统比对）', '学历证书核实（学信网查询）', '工作经历核实（企业信息公示）', '视频1对1身份确认', '审核通过，开放全部功能'].map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-amber-700">
                <div className="w-4 h-4 flex items-center justify-center bg-amber-200 rounded-full text-amber-800 font-bold flex-shrink-0">{i + 1}</div>
                {s}
              </div>
            ))}
          </div>
        </div>
        <Link to="/" className="inline-block px-8 py-3 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-1 mb-3">
        {([1, 2, 3, 4] as Step[]).map((s) => (
          <div key={s} className="flex items-center gap-1">
            <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold transition-all ${step >= s ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
              {step > s ? <i className="ri-check-line text-xs"></i> : s}
            </div>
            {s < 4 && <div className={`w-8 h-0.5 transition-all ${step > s ? 'bg-red-400' : 'bg-gray-200'}`}></div>}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-400 mb-5 px-1">
        {([1, 2, 3, 4] as Step[]).map((s) => (
          <span key={s} className={`text-center ${step >= s ? 'text-red-600 font-medium' : ''}`} style={{ width: '25%' }}>{STEP_LABELS[s]}</span>
        ))}
      </div>

      {/* ── STEP 1: Basic Info ── */}
      {step === 1 && (
        <div className="flex flex-col gap-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {(['phone', 'email'] as RegMethod[]).map((m) => (
              <button key={m} type="button" onClick={() => setMethod(m)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${method === m ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500'}`}>
                {m === 'phone' ? <><i className="ri-smartphone-line mr-1"></i>手机号</> : <><i className="ri-mail-line mr-1"></i>邮箱</>}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">昵称 <span className="text-red-500">*</span></label>
              <input type="text" placeholder="您的昵称" value={formData.nickname}
                onChange={(e) => handleChange('nickname', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">真实姓名 <span className="text-red-500">*</span></label>
              <input type="text" placeholder="与证件完全一致" value={formData.realName}
                onChange={(e) => handleChange('realName', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">性别 <span className="text-red-500">*</span></label>
              <select value={formData.gender} onChange={(e) => handleChange('gender', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
                <option value="">请选择</option>
                <option value="male">男</option>
                <option value="female">女</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">出生年份 <span className="text-red-500">*</span></label>
              <select value={formData.birthYear} onChange={(e) => handleChange('birthYear', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
                <option value="">请选择</option>
                {Array.from({ length: 50 }, (_, i) => 2005 - i).map((y) => (
                  <option key={y} value={y}>{y}年</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">所在城市 <span className="text-red-500">*</span></label>
            <input type="text" placeholder="如：北京、上海、广州" value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
          </div>

          {method === 'phone' ? (
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">手机号码 <span className="text-red-500">*</span></label>
              <div className="flex gap-2">
                <select value={formData.countryCode} onChange={(e) => handleChange('countryCode', e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer w-24">
                  <option value="+86">+86 🇨🇳</option>
                  <option value="+1">+1 🇺🇸</option>
                  <option value="+44">+44 🇬🇧</option>
                  <option value="+61">+61 🇦🇺</option>
                  <option value="+65">+65 🇸🇬</option>
                  <option value="+852">+852 🇭🇰</option>
                  <option value="+886">+886 🇹🇼</option>
                </select>
                <input type="tel" placeholder="请输入手机号" value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
              </div>
              <div className="flex gap-2 mt-2">
                <input type="text" placeholder="请输入短信验证码" value={formData.smsCode}
                  onChange={(e) => handleChange('smsCode', e.target.value)} maxLength={6}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
                <button type="button" onClick={sendCode} disabled={countdown > 0}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${countdown > 0 ? 'bg-gray-100 text-gray-400' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}>
                  {countdown > 0 ? `${countdown}s` : codeSent ? '重新发送' : '发送验证码'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1.5 block">邮箱地址 <span className="text-red-500">*</span></label>
                <input type="email" placeholder="请输入邮箱地址" value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1.5 block">设置密码 <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} placeholder="至少8位，含字母和数字" value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer">
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>
            </>
          )}

          <button type="button" onClick={() => setStep(2)}
            disabled={!formData.nickname || !formData.realName || !formData.gender || !formData.birthYear || !formData.city}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-full font-semibold text-sm hover:from-red-700 hover:to-red-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mt-1">
            下一步：学历与工作经历 <i className="ri-arrow-right-line ml-1"></i>
          </button>
          <p className="text-center text-xs text-gray-400">
            已有账号？<button type="button" onClick={onSwitch} className="text-red-500 font-medium ml-1 cursor-pointer hover:underline">立即登录</button>
          </p>
        </div>
      )}

      {/* ── STEP 2: Education + Work ── */}
      {step === 2 && (
        <div className="flex flex-col gap-5">
          {/* Platform Integrity Notice */}
          <div className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <i className="ri-award-line text-red-500 text-lg mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-sm font-bold text-red-700 mb-1">富缘佳旺真实承诺</p>
                <p className="text-xs text-red-600 leading-relaxed">本平台 <strong>100% 真实认证</strong>。所有学历经学信网核实，工作经历经企业公示系统核实。虚假信息将永久封号，确保每位会员遇见的都是真实的缘分。</p>
              </div>
            </div>
          </div>

          {/* Education Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 flex items-center justify-center bg-yellow-100 rounded-lg">
                <i className="ri-graduation-cap-line text-yellow-600 text-sm"></i>
              </div>
              <h4 className="text-sm font-bold text-gray-800">最高学历信息 <span className="text-red-500">*</span></h4>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">学历层次</label>
                  <select value={formData.eduLevel} onChange={(e) => handleChange('eduLevel', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer bg-white">
                    <option value="">请选择</option>
                    <option value="phd">博士研究生</option>
                    <option value="master">硕士研究生</option>
                    <option value="bachelor">本科</option>
                    <option value="college">大专</option>
                    <option value="highschool">高中</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">毕业年份</label>
                  <select value={formData.eduGradYear} onChange={(e) => handleChange('eduGradYear', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer bg-white">
                    <option value="">请选择</option>
                    {Array.from({ length: 30 }, (_, i) => 2026 - i).map((y) => (
                      <option key={y} value={y}>{y}年</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium mb-1.5 block">毕业院校</label>
                <input type="text" placeholder="如：清华大学、北京大学、复旦大学" value={formData.eduSchool}
                  onChange={(e) => handleChange('eduSchool', e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">所学专业</label>
                  <input type="text" placeholder="如：计算机科学、金融学" value={formData.eduMajor}
                    onChange={(e) => handleChange('eduMajor', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 bg-white" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">学位类型</label>
                  <select value={formData.eduDegree} onChange={(e) => handleChange('eduDegree', e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer bg-white">
                    <option value="">请选择</option>
                    <option value="academic">学术型</option>
                    <option value="professional">专业型</option>
                    <option value="bachelor_degree">学士学位</option>
                    <option value="none">无学位</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Education Document Upload */}
          <div>
            <label className="text-xs text-gray-500 font-medium mb-2 block">
              <i className="ri-file-text-line mr-1 text-yellow-500"></i>
              上传学历证书 / 毕业证 <span className="text-red-500">*</span>
              <span className="text-gray-400 ml-1">（将通过学信网核实）</span>
            </label>
            <div onClick={() => diplomaRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-all h-24 ${diplomaDoc ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-yellow-400 bg-gray-50'}`}>
              {diplomaDoc ? (
                <div className="flex items-center justify-center h-full gap-3">
                  <i className="ri-file-check-line text-green-500 text-2xl"></i>
                  <div>
                    <p className="text-sm font-semibold text-green-700">已上传</p>
                    <p className="text-xs text-green-500">{diplomaDoc.name}</p>
                  </div>
                  <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full gap-3">
                  <i className="ri-upload-cloud-line text-gray-400 text-2xl"></i>
                  <div>
                    <p className="text-sm text-gray-500">点击上传学历证书或毕业证</p>
                    <p className="text-xs text-gray-400">支持 JPG、PNG、PDF，小于20MB</p>
                  </div>
                </div>
              )}
            </div>
            <input ref={diplomaRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, setDiplomaDoc)} />
          </div>

          <div>
            <label className="text-xs text-gray-500 font-medium mb-2 block">
              <i className="ri-file-list-line mr-1 text-yellow-500"></i>
              上传成绩单（可选）
            </label>
            <div onClick={() => transcriptRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-all h-20 ${transcriptDoc ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-yellow-400 bg-gray-50'}`}>
              {transcriptDoc ? (
                <div className="flex items-center justify-center h-full gap-3">
                  <i className="ri-file-check-line text-green-500 text-xl"></i>
                  <p className="text-sm font-semibold text-green-700">{transcriptDoc.name}</p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full gap-2">
                  <i className="ri-upload-line text-gray-400"></i>
                  <p className="text-sm text-gray-400">点击上传成绩单（可选，提升可信度）</p>
                </div>
              )}
            </div>
            <input ref={transcriptRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, setTranscriptDoc)} />
          </div>

          {/* Work Experience Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-red-100 rounded-lg">
                  <i className="ri-briefcase-line text-red-500 text-sm"></i>
                </div>
                <h4 className="text-sm font-bold text-gray-800">工作经历 <span className="text-red-500">*</span></h4>
              </div>
              {workHistory.length < 4 && (
                <button type="button" onClick={addWork}
                  className="text-xs text-red-500 flex items-center gap-1 cursor-pointer hover:text-red-700">
                  <i className="ri-add-line"></i> 添加经历
                </button>
              )}
            </div>

            <div className="flex flex-col gap-4">
              {workHistory.map((work, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-4 relative">
                  {workHistory.length > 1 && (
                    <button type="button" onClick={() => removeWork(idx)}
                      className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer">
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  )}
                  <div className="text-xs font-semibold text-gray-500 mb-3">工作经历 {idx + 1}</div>
                  <div className="flex flex-col gap-2.5">
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">公司/单位名称</label>
                      <input type="text" placeholder="如：阿里巴巴集团、北京协和医院" value={work.company}
                        onChange={(e) => updateWork(idx, 'company', e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">职位/职称</label>
                      <input type="text" placeholder="如：高级工程师、主治医师、产品经理" value={work.title}
                        onChange={(e) => updateWork(idx, 'title', e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">入职年份</label>
                        <select value={work.startYear} onChange={(e) => updateWork(idx, 'startYear', e.target.value)}
                          className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-red-400 cursor-pointer bg-white">
                          <option value="">请选择</option>
                          {Array.from({ length: 30 }, (_, i) => 2026 - i).map((y) => (
                            <option key={y} value={y}>{y}年</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">离职年份</label>
                        <select value={work.current ? 'current' : work.endYear}
                          onChange={(e) => {
                            if (e.target.value === 'current') { updateWork(idx, 'current', true); updateWork(idx, 'endYear', ''); }
                            else { updateWork(idx, 'current', false); updateWork(idx, 'endYear', e.target.value); }
                          }}
                          className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-red-400 cursor-pointer bg-white">
                          <option value="">请选择</option>
                          <option value="current">至今（在职）</option>
                          {Array.from({ length: 30 }, (_, i) => 2026 - i).map((y) => (
                            <option key={y} value={y}>{y}年</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">工作描述（可选）</label>
                      <textarea placeholder="简述主要工作职责和成就..." value={work.description}
                        onChange={(e) => { if (e.target.value.length <= 200) updateWork(idx, 'description', e.target.value); }}
                        rows={2} maxLength={200}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 resize-none bg-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Work Document Upload */}
          <div>
            <label className="text-xs text-gray-500 font-medium mb-2 block">
              <i className="ri-building-line mr-1 text-red-400"></i>
              上传在职证明 / 工作证 <span className="text-red-500">*</span>
              <span className="text-gray-400 ml-1">（将通过企业公示系统核实）</span>
            </label>
            <div onClick={() => workDocRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-all h-24 ${workDoc ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-red-300 bg-gray-50'}`}>
              {workDoc ? (
                <div className="flex items-center justify-center h-full gap-3">
                  <i className="ri-file-check-line text-green-500 text-2xl"></i>
                  <div>
                    <p className="text-sm font-semibold text-green-700">已上传</p>
                    <p className="text-xs text-green-500">{workDoc.name}</p>
                  </div>
                  <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-xs"></i>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full gap-3">
                  <i className="ri-upload-cloud-line text-gray-400 text-2xl"></i>
                  <div>
                    <p className="text-sm text-gray-500">点击上传在职证明或工作证</p>
                    <p className="text-xs text-gray-400">支持 JPG、PNG、PDF，小于20MB</p>
                  </div>
                </div>
              )}
            </div>
            <input ref={workDocRef} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileChange(e, setWorkDoc)} />
          </div>

          {/* Income */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">当前职业</label>
              <input type="text" placeholder="如：工程师、医生" value={formData.occupation}
                onChange={(e) => handleChange('occupation', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1.5 block">年收入范围</label>
              <select value={formData.income} onChange={(e) => handleChange('income', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
                <option value="">请选择</option>
                <option value="10以下">10万以下</option>
                <option value="10-20万">10-20万</option>
                <option value="20-30万">20-30万</option>
                <option value="30-50万">30-50万</option>
                <option value="50-80万">50-80万</option>
                <option value="80万以上">80万以上</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(1)}
              className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> 上一步
            </button>
            <button type="button" onClick={() => setStep(3)}
              disabled={!formData.eduLevel || !formData.eduSchool || !formData.eduMajor || !diplomaDoc || !workDoc || !workHistory[0].company || !workHistory[0].title}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-full font-semibold text-sm hover:from-red-700 hover:to-red-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
              下一步：身份认证 <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: ID Verification ── */}
      {step === 3 && (
        <div className="flex flex-col gap-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <i className="ri-shield-user-line text-amber-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">实名认证说明</p>
                <p className="text-xs text-amber-700">上传真实有效的身份证件，确保昵称「{formData.nickname}」与真实姓名「{formData.realName}」为同一人。证件信息仅用于身份核验，严格加密保密。</p>
              </div>
            </div>
          </div>

          {[
            { label: '身份证正面（人像面）', ref: idFrontRef, state: idFront, setter: setIdFront, icon: 'ri-id-card-line' },
            { label: '身份证背面（国徽面）', ref: idBackRef, state: idBack, setter: setIdBack, icon: 'ri-government-line' },
          ].map((item, i) => (
            <div key={i}>
              <label className="text-xs text-gray-500 font-medium mb-2 block">
                <i className={`${item.icon} mr-1 text-red-500`}></i>
                {item.label} <span className="text-red-500">*</span>
              </label>
              <div onClick={() => item.ref.current?.click()}
                className={`relative border-2 border-dashed rounded-xl overflow-hidden cursor-pointer transition-all ${item.state ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-red-300 bg-gray-50'}`}
                style={{ height: '130px' }}>
                {item.state ? (
                  <div className="relative w-full h-full">
                    <img src={item.state.preview} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-xs font-medium">点击重新上传</span>
                    </div>
                    <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
                      <i className="ri-check-line text-xs"></i>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full">
                      <i className={`${item.icon} text-red-400 text-lg`}></i>
                    </div>
                    <p className="text-xs text-gray-400">点击上传 · 支持 JPG、PNG，小于10MB</p>
                  </div>
                )}
              </div>
              <input ref={item.ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileChange(e, item.setter)} />
            </div>
          ))}

          <div className="bg-red-50 border border-red-100 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <i className="ri-video-chat-line text-red-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-red-700 mb-1">视频认证说明</p>
                <p className="text-xs text-red-600">证件审核通过后，您需完成一次 <strong>1分钟视频认证</strong>，由工作人员确认您与证件照片为同一人。视频认证通过后，您的资料将显示 <strong>「视频已认证」</strong> 徽章，大幅提升匹配成功率。</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(2)}
              className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> 上一步
            </button>
            <button type="button" onClick={() => setStep(4)} disabled={!idFront || !idBack}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-full font-semibold text-sm hover:from-red-700 hover:to-red-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
              下一步：上传照片 <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: Life Photos + Submit ── */}
      {step === 4 && (
        <div className="flex flex-col gap-5">
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-4">
            <div className="flex items-start gap-2">
              <i className="ri-camera-line text-rose-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-rose-800 mb-1">生活照上传说明</p>
                <p className="text-xs text-rose-700">请上传 <strong>2-6张</strong> 近期生活照，需为本人真实照片，清晰展示面部。照片将展示在您的个人资料页，帮助其他会员了解真实的您。</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 font-medium mb-2 block">
              生活照片 <span className="text-red-500">*</span>
              <span className="text-gray-400 ml-1">({lifePhotos.length}/6，至少2张)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {lifePhotos.map((photo, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden bg-gray-100" style={{ aspectRatio: '3/4' }}>
                  <img src={photo.preview} alt={`Life photo ${idx + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeLifePhoto(idx)}
                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-black/50 rounded-full text-white cursor-pointer hover:bg-black/70 transition-all">
                    <i className="ri-close-line text-xs"></i>
                  </button>
                  {idx === 0 && (
                    <div className="absolute bottom-0 left-0 right-0 bg-red-600/80 text-white text-xs text-center py-1">主照片</div>
                  )}
                </div>
              ))}
              {lifePhotos.length < 6 && (
                <div onClick={() => lifePhotosRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-red-300 hover:bg-red-50 transition-all"
                  style={{ aspectRatio: '3/4' }}>
                  <i className="ri-add-line text-gray-400 text-2xl"></i>
                  <span className="text-xs text-gray-400 mt-1">添加照片</span>
                </div>
              )}
            </div>
            <input ref={lifePhotosRef} type="file" accept="image/*" multiple className="hidden" onChange={handleLifePhotos} />
          </div>

          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">个人简介</label>
            <textarea placeholder="简单介绍一下自己，让对方更了解您..." value={formData.selfIntro}
              onChange={(e) => { if (e.target.value.length <= 500) handleChange('selfIntro', e.target.value); }}
              rows={3} maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 resize-none" />
            <div className="text-right text-xs text-gray-400 mt-1">{formData.selfIntro.length}/500</div>
          </div>

          {/* Full Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-bold text-gray-600 mb-3">注册信息确认</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
              {[
                { icon: 'ri-check-line', color: 'text-green-500', label: `昵称：${formData.nickname}` },
                { icon: 'ri-check-line', color: 'text-green-500', label: `真实姓名：${formData.realName}` },
                { icon: 'ri-check-line', color: 'text-green-500', label: `学历：${eduLabel(formData.eduLevel)}` },
                { icon: 'ri-check-line', color: 'text-green-500', label: `院校：${formData.eduSchool || '未填'}` },
                { icon: diplomaDoc ? 'ri-check-line' : 'ri-time-line', color: diplomaDoc ? 'text-green-500' : 'text-amber-500', label: `学历证书：${diplomaDoc ? '已上传' : '未上传'}` },
                { icon: workDoc ? 'ri-check-line' : 'ri-time-line', color: workDoc ? 'text-green-500' : 'text-amber-500', label: `在职证明：${workDoc ? '已上传' : '未上传'}` },
                { icon: idFront && idBack ? 'ri-check-line' : 'ri-time-line', color: idFront && idBack ? 'text-green-500' : 'text-amber-500', label: `身份证：${idFront && idBack ? '已上传' : '未上传'}` },
                { icon: lifePhotos.length >= 2 ? 'ri-check-line' : 'ri-time-line', color: lifePhotos.length >= 2 ? 'text-green-500' : 'text-amber-500', label: `生活照：${lifePhotos.length}张` },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <i className={`${item.icon} ${item.color}`}></i>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(3)}
              className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line mr-1"></i> 上一步
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading || lifePhotos.length < 2}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-full font-semibold text-sm hover:from-red-700 hover:to-red-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>提交中...</> : '提交注册申请'}
            </button>
          </div>

          <p className="text-center text-xs text-gray-400">
            提交即表示同意
            <a href="#" className="text-red-500 mx-1 cursor-pointer">《用户协议》</a>和
            <a href="#" className="text-red-500 mx-1 cursor-pointer">《隐私政策》</a>
          </p>
        </div>
      )}
    </div>
  );
}
